import { baseApi } from './baseApi';
import { User, CreateUserData, UpdateUserData } from '../types/user';

export interface UsersResponse {
  users: User[];
}

export interface UserResponse {
  user: User;
}

export interface MessageResponse {
  message: string;
}

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<UsersResponse, void>({
      query: () => '/auth/users',
      providesTags: (result) =>
        result?.users
          ? [
            ...result.users.map(({ id }) => ({ type: 'User' as const, id })),
            { type: 'User', id: 'LIST' },
          ]
          : [{ type: 'User', id: 'LIST' }],
    }),

    getUserById: builder.query<UserResponse, string>({
      query: (userId) => `/auth/user/${userId}`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),

    createUser: builder.mutation<MessageResponse, CreateUserData>({
      query: (userData) => ({
        url: '/auth/create-user',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    updateUser: builder.mutation<MessageResponse, { id: string; userData: UpdateUserData }>({
      query: ({ id, userData }) => ({
        url: '/auth/update-user',
        method: 'PUT',
        body: { id, ...userData },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    deleteUser: builder.mutation<MessageResponse, string>({
      query: (userId) => ({
        url: `/auth/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    // All users management logs
    getUserLogs: builder.query<any, void>({
      query: () => `auth/users/logs`,
    }),

    // Specific user logs
    getUserLogsById: builder.query<any, string>({
      query: (userId) => `/auth/user/${userId}/logs`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserLogsQuery,
  useGetUserLogsByIdQuery,
} = userApi;
